import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import InternshipPage from "./pages/InternshipPage";
import PortfolioPage from "./pages/PortfolioPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import CareersPage from "./pages/CareersPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ContactPage from "./pages/ContactPage";
import { prefetchSheet } from "./hooks/useSheetData";
import "./styles/global.css";

// Warm the sheet-data cache for every known tab as soon as the site opens,
// so by the time a visitor navigates to another sheet-backed page, the
// slow Apps Script round-trip has likely already finished. Apps Script
// can't actually run many requests to the same script concurrently —
// firing all of them at once just makes them queue behind each other,
// so whichever tab loses the race pays for everyone else's latency too.
// Instead: fetch whatever the CURRENT page needs first, then stagger the
// rest a beat apart so they queue in a sane order instead of all at once.
const SHEET_NAMES = ["Jobs", "Blog", "Stats", "Portfolio"];
const SHEET_FOR_PATH = { "/": "Stats", "/careers": "Jobs", "/blog": "Blog", "/portfolio": "Portfolio" };

function PrefetchSheets() {
  const { pathname } = useLocation();
  useEffect(() => {
    const priority = SHEET_FOR_PATH[pathname];
    const rest = SHEET_NAMES.filter((name) => name !== priority);
    if (priority) prefetchSheet(priority);
    rest.forEach((name, i) => setTimeout(() => prefetchSheet(name), (i + 1) * 400));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only on initial load — later navigation is handled by each page's own useSheetData call
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);
  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -14 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      >
        <Routes location={location}>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/internship" element={<InternshipPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/portfolio/:slug" element={<ProjectDetailPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <PrefetchSheets />
      <ScrollToTop />
      <Navbar />
      <main style={{ minHeight: "100vh" }}>
        <AnimatedRoutes />
      </main>
      <Footer />
    </BrowserRouter>
  );
}
